import { FormEvent, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import client from '@/http/customer-client'
import { Button } from '@/components/customer-ui/button'
import { Input } from '@/components/customer-ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

type ChangePasswordDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type ChangePasswordErrorResponse = {
  message?: string
  errors?: Record<string, string[] | string>
}

type RequiredFieldErrors = {
  oldPassword: boolean
  newPassword: boolean
  confirmNewPassword: boolean
}

const fieldLabels: Record<string, string> = {
  current_password: 'oude wachtwoord',
  new_password: 'nieuwe wachtwoord',
  new_password_confirmation: 'bevestiging van nieuwe wachtwoord'
}

function hasAny(text: string, fragments: string[]) {
  return fragments.some((fragment) => text.includes(fragment))
}

function mapValidationMessageToDutch(
  message: string,
  t: (key: string, options?: Record<string, string>) => string,
  field?: string
) {
  const normalized = message.toLowerCase()

  if (
    hasAny(normalized, ['validation.required', ' required', 'is required']) ||
    normalized === 'required'
  ) {
    if (field && fieldLabels[field]) {
      return t('page_change_password.error.required_field', {
        field: fieldLabels[field]
      })
    }
    return t('page_change_password.error.required')
  }

  if (
    field === 'current_password' &&
    hasAny(normalized, [
      'validation.current_password',
      'auth.password',
      'password is incorrect',
      'provided password is incorrect',
      'incorrect',
      'invalid'
    ])
  ) {
    return t('page_change_password.error.current_password_invalid')
  }

  if (
    field === 'new_password_confirmation' &&
    hasAny(normalized, [
      'validation.confirmed',
      'not match',
      "doesn't match",
      'does not match'
    ])
  ) {
    return t('page_change_password.error.confirmed')
  }

  if (
    field === 'new_password' &&
    hasAny(normalized, [
      'validation.min',
      'at least',
      'too short',
      'minimum',
      'min:'
    ])
  ) {
    return t('page_change_password.error.new_password_min')
  }

  if (
    hasAny(normalized, [
      'validation.current_password',
      'validation.confirmed',
      'validation.min',
      'validation.'
    ])
  ) {
    return t('page_change_password.error.validation')
  }

  if (field === 'current_password') {
    return t('page_change_password.error.current_password_invalid')
  }

  return t('page_change_password.error.generic')
}

function resolveDutchErrorMessage(
  t: (key: string, options?: Record<string, string>) => string,
  data?: ChangePasswordErrorResponse
) {
  const fallback = [t('page_change_password.error.generic')]

  if (data == null) {
    return fallback
  }

  const allErrors = data.errors
  if (allErrors && typeof allErrors === 'object') {
    const collected = Object.entries(allErrors)
      .flatMap(([field, value]) => {
        const messages = Array.isArray(value) ? value : [value]
        return messages.map((item) =>
          mapValidationMessageToDutch(item, t, field)
        )
      })
      .filter((item) => item.trim() !== '')

    if (collected.length > 0) {
      return [...new Set(collected)]
    }
  }

  if (typeof data.message === 'string' && data.message.trim() !== '') {
    return [mapValidationMessageToDutch(data.message, t)]
  }

  return fallback
}

function mapApiFieldToFormField(
  field: string
): keyof RequiredFieldErrors | null {
  if (field === 'current_password') return 'oldPassword'
  if (field === 'new_password') return 'newPassword'
  if (field === 'new_password_confirmation') return 'confirmNewPassword'
  return null
}

function getRequiredFieldErrorsFromApi(
  data?: ChangePasswordErrorResponse
): RequiredFieldErrors {
  const next: RequiredFieldErrors = {
    oldPassword: false,
    newPassword: false,
    confirmNewPassword: false
  }

  if (!data?.errors || typeof data.errors !== 'object') {
    return next
  }

  Object.entries(data.errors).forEach(([field, value]) => {
    const formField = mapApiFieldToFormField(field)
    if (formField == null) return

    const messages = Array.isArray(value) ? value : [value]
    const hasRequired = messages.some((message) => {
      const normalized = message.toLowerCase()
      return (
        hasAny(normalized, [
          'validation.required',
          ' required',
          'is required'
        ]) || normalized === 'required'
      )
    })

    if (hasRequired) {
      next[formField] = true
    }
  })

  return next
}

export default function ChangePasswordDialog({
  open,
  onOpenChange
}: ChangePasswordDialogProps) {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [requiredFieldErrors, setRequiredFieldErrors] =
    useState<RequiredFieldErrors>({
      oldPassword: false,
      newPassword: false,
      confirmNewPassword: false
    })
  const [form, setForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  })
  const [showPassword, setShowPassword] = useState({
    old: false,
    next: false,
    confirm: false
  })

  const resetForm = () => {
    setForm({
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    })
    setShowPassword({
      old: false,
      next: false,
      confirm: false
    })
    setErrors([])
    setRequiredFieldErrors({
      oldPassword: false,
      newPassword: false,
      confirmNewPassword: false
    })
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrors([])
    setRequiredFieldErrors({
      oldPassword: false,
      newPassword: false,
      confirmNewPassword: false
    })

    const missingOldPassword = form.oldPassword.trim() === ''
    const missingNewPassword = form.newPassword.trim() === ''
    const missingConfirmNewPassword = form.confirmNewPassword.trim() === ''

    if (missingOldPassword || missingNewPassword || missingConfirmNewPassword) {
      setRequiredFieldErrors({
        oldPassword: missingOldPassword,
        newPassword: missingNewPassword,
        confirmNewPassword: missingConfirmNewPassword
      })
      setErrors([t('page_change_password.error.required')])
      return
    }

    if (form.newPassword !== form.confirmNewPassword) {
      setErrors([t('page_change_password.error.confirmed')])
      return
    }

    setIsSaving(true)
    try {
      await client.put('/app-api/change-password', {
        current_password: form.oldPassword,
        new_password: form.newPassword,
        new_password_confirmation: form.confirmNewPassword
      })

      onOpenChange(false)
      resetForm()
      setShowSuccessDialog(true)
    } catch (e) {
      if (isAxiosError<ChangePasswordErrorResponse>(e)) {
        const apiData = e.response?.data
        setErrors(resolveDutchErrorMessage(t, apiData))
        setRequiredFieldErrors(getRequiredFieldErrorsFromApi(apiData))
      } else {
        setErrors([t('page_change_password.error.generic')])
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen)
          if (!nextOpen) {
            resetForm()
          }
        }}
      >
        <DialogContent className='rounded-xl p-6 sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Wachtwoord veranderen</DialogTitle>
          </DialogHeader>

          <form
            noValidate
            onSubmit={(e) => void onSubmit(e)}
            className='space-y-4'
          >
            <div className='space-y-2'>
              <Label htmlFor='old-password'>
                Oude wachtwoord <span className='text-destructive'>*</span>
              </Label>
              <div className='relative'>
                <Input
                  id='old-password'
                  type={showPassword.old ? 'text' : 'password'}
                  placeholder='Uw wachtwoord'
                  value={form.oldPassword}
                  onChange={(event) => {
                    setForm((prev) => ({
                      ...prev,
                      oldPassword: event.target.value
                    }))
                    setRequiredFieldErrors((prev) => ({
                      ...prev,
                      oldPassword: false
                    }))
                  }}
                  className={`h-12 pr-12 ${
                    requiredFieldErrors.oldPassword
                      ? 'border-destructive focus-visible:ring-destructive'
                      : ''
                  }`}
                  style={
                    requiredFieldErrors.oldPassword
                      ? { borderColor: 'var(--destructive)' }
                      : undefined
                  }
                  aria-invalid={requiredFieldErrors.oldPassword}
                  autoComplete='current-password'
                />
                <button
                  type='button'
                  onClick={() => {
                    setShowPassword((prev) => ({ ...prev, old: !prev.old }))
                  }}
                  className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2'
                >
                  {showPassword.old ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='new-password'>
                Nieuwe wachtwoord <span className='text-destructive'>*</span>
              </Label>
              <div className='relative'>
                <Input
                  id='new-password'
                  type={showPassword.next ? 'text' : 'password'}
                  placeholder='Uw wachtwoord'
                  value={form.newPassword}
                  onChange={(event) => {
                    setForm((prev) => ({
                      ...prev,
                      newPassword: event.target.value
                    }))
                    setRequiredFieldErrors((prev) => ({
                      ...prev,
                      newPassword: false
                    }))
                  }}
                  className={`h-12 pr-12 ${
                    requiredFieldErrors.newPassword
                      ? 'border-destructive focus-visible:ring-destructive'
                      : ''
                  }`}
                  style={
                    requiredFieldErrors.newPassword
                      ? { borderColor: 'var(--destructive)' }
                      : undefined
                  }
                  aria-invalid={requiredFieldErrors.newPassword}
                  autoComplete='new-password'
                />
                <button
                  type='button'
                  onClick={() => {
                    setShowPassword((prev) => ({ ...prev, next: !prev.next }))
                  }}
                  className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2'
                >
                  {showPassword.next ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='confirm-new-password'>
                Bevestig nieuwe wachtwoord{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <div className='relative'>
                <Input
                  id='confirm-new-password'
                  type={showPassword.confirm ? 'text' : 'password'}
                  placeholder='Uw wachtwoord'
                  value={form.confirmNewPassword}
                  onChange={(event) => {
                    setForm((prev) => ({
                      ...prev,
                      confirmNewPassword: event.target.value
                    }))
                    setRequiredFieldErrors((prev) => ({
                      ...prev,
                      confirmNewPassword: false
                    }))
                  }}
                  className={`h-12 pr-12 ${
                    requiredFieldErrors.confirmNewPassword
                      ? 'border-destructive focus-visible:ring-destructive'
                      : ''
                  }`}
                  style={
                    requiredFieldErrors.confirmNewPassword
                      ? { borderColor: 'var(--destructive)' }
                      : undefined
                  }
                  aria-invalid={requiredFieldErrors.confirmNewPassword}
                  autoComplete='new-password'
                />
                <button
                  type='button'
                  onClick={() => {
                    setShowPassword((prev) => ({
                      ...prev,
                      confirm: !prev.confirm
                    }))
                  }}
                  className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2'
                >
                  {showPassword.confirm ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            {errors.length > 0 && (
              <div className='space-y-1'>
                {errors.map((item) => (
                  <p key={item} className='text-destructive text-sm'>
                    {item}
                  </p>
                ))}
              </div>
            )}

            <Button
              type='submit'
              className='h-12 w-full text-base font-semibold'
              disabled={isSaving}
            >
              {isSaving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent overlay={false}>
          <AlertDialogHeader>
            <AlertDialogTitle>Opgeslagen</AlertDialogTitle>
            <AlertDialogDescription>
              Uw wachtwoord is opgeslagen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setShowSuccessDialog(false)
              }}
            >
              Ok
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
